<template>
  <div class="action-bar">
    <Button @click="generatePDF">
      Generate PDF
    </Button>

    <Dropdown
      v-model="selectedStyle"
      :options="styles"
      option-label="name"
      @change="updateStyle"
    />
  </div>

  <div
    ref="quoteContent"
    class="quote-container"
  >
    <!-- Header with Logo and Quotation Label -->
    <div class="header flex justify-between items-center">
      <img
        :src="Logo"
        alt="Symphony Logo"
        class="logo"
      >
      <div
        class="quote-box"
        :style="{ backgroundColor: mainColour, color: tableFontColour }"
      >
        QUOTATION
        <!-- Triangle as a div instead of pseudo-element for dynamic color -->
        <div
          class="quote-triangle"
          :style="{ borderTopColor: mainColour }"
        />
      </div>
    </div>

    <!-- Invoice and Customer Information -->
    <div class="info-section flex justify-between">
      <div>
        <p>Mr. John Doe</p>
        <p>14, Dummy Street Area, Location</p>
        <p>Lorem Ipsum, 055 666</p>
      </div>
      <div class="quote-number">
        <p><strong>Quote No:</strong> #12345</p>
        <p><strong>Date:</strong> 25-11-2024</p>
      </div>
    </div>

    <!-- Product Details Table -->
    <table
      class="product-table w-full"
      :style="{ borderColor: tableColour }"
    >
      <thead :style="{ backgroundColor: mainColour, color: tableFontColour }">
        <tr>
          <th>No.</th>
          <th>Product</th>
          <th>Price/Day</th>
          <th>Rental Duration</th>
          <th>Replacement Charges</th>
          <th>Rental Charges</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in items"
          :key="index"
          :style="{ borderColor: tableColour }"
        >
          <td>{{ index + 1 }}</td>
          <td>{{ item.product }}</td>
          <td>£{{ item.pricePerDay }}</td>
          <td>{{ item.duration }}</td>
          <td>£{{ item.replacementCharges }}</td>
          <td>£{{ item.rentalCharges }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Payment Info and Totals -->
    <div class="payment-section flex justify-between">
      <!-- Payment Info -->
      <div class="justify-content-end">
        <h3>Payment Info:</h3>
        <p>Account No: 1234 5678 1234</p>
        <p>A/C Name: John Smith</p>
      </div>
      <!-- Totals -->
      <div>
        <table
          class="totals-table"
          :style="{ backgroundColor: mainColour, borderColor: tableColour, color: tableFontColour }"
        >
          <tr>
            <td>Total</td>
            <td>£2240</td>
          </tr>
          <tr>
            <td>Discount (10%)</td>
            <td>£224</td>
          </tr>
          <tr>
            <td><strong>Discounted Total</strong></td>
            <td><strong>£2016</strong></td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Contact Information -->
    <div class="contact-info mt-4">
      <div>
        <i
          class="pi pi-phone"
          :style="{ color: mainColour }"
        /><strong>01234 123456</strong>
      </div>
      <div>
        <i
          class="pi pi-envelope"
          :style="{ color: mainColour }"
        /><strong>test@xzy.com</strong>
      </div>
    </div>

    <!-- Signature Section -->
    <div class="signature-section text-center mt-1">
      <p>Authorised Signature</p>
      <div class="signature-line mt-5" />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Logo from '@/assets/logos/Symphony_Logo_Vertical_RGB.png';

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const quoteContent = ref(null)

//When a new style is picked from the dropdown
const updateStyle = (event) => {
    mainColour.value = event.value.mainCol
    tableColour.value = event.value.tableCol
    tableFontColour.value = event.value.tableFontCol
}


const mainColour = ref('#ffcc00'); // Dynamic color for main elements
const tableColour = ref('grey'); // Dynamic color for table border
const tableFontColour = ref('black')

const styles = ref([
    {
        'name': 'Yellow',
        'mainCol': '#ffcc00',
        'tableCol': 'grey',
        'tableFontCol': 'black',
    },
    {
        'name': 'Orange',
        'mainCol': 'orange',
        'tableCol': 'grey',
        'tableFontCol': 'black',
    }, {
        'name': 'Blue',
        'mainCol': 'blue',
        'tableCol': 'grey',
        'tableFontCol': 'white',
    },
    {
        'name': 'black',
        'mainCol': 'black',
        'tableCol': 'black',
        'tableFontCol': 'white',
    }
])

const items = ref([
    { product: 'Product A', pricePerDay: 50, duration: '7 Days', replacementCharges: 100, rentalCharges: 350 },
    { product: 'Product B', pricePerDay: 70, duration: '7 Days', replacementCharges: 100, rentalCharges: 490 },
    { product: 'Product C', pricePerDay: 80, duration: '7 Days', replacementCharges: 100, rentalCharges: 560 },
    { product: 'Product D', pricePerDay: 50, duration: '7 Days', replacementCharges: 100, rentalCharges: 350 },
    { product: 'Product E', pricePerDay: 70, duration: '7 Days', replacementCharges: 100, rentalCharges: 490 },
])
const generatePDF = async () => {
    const content = quoteContent.value
    const canvas = await html2canvas(content)
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
    pdf.save('quote.pdf')
}

</script>

<style scoped>
.action-bar {
    width: 210mm;
    /* Match the width of the quote-container */
    margin: 0 auto 20px;
    /* Centered on the page */
    display: flex;
    justify-content: space-between;
    align-items: center;
}

i {
    padding: 3mm
}

/* Set quote-container to A4 size */
.quote-container {
    width: 210mm;
    height: 297mm;
    margin: 0 auto;
    padding: 20px;
    font-family: Arial, sans-serif;
    color: #333;
    background-color: #f9f9f9;
    border: 1px solid #e0e0e0;
    position: relative;
}

/* Position header content */
.header {
    margin-bottom: 20px;
}

.logo {
    width: 50mm;
    /* Set logo size in mm for consistency */
}


/* QUOTATION box with exact positioning in mm */
.quote-box {
    position: absolute;
    top: 10mm;
    /* Adjust distance from the top */
    right: 10mm;
    /* Adjust distance from the right edge of the page */
    color: #000;
    font-weight: bold;
    padding: 10px 20px;
    font-size: 18px;
    text-align: center;
    border-radius: 4px 4px 0 0;
}

.quote-number {
    position: absolute;
    top: 50mm;
    right: 6mm;
    text-align: right;
}

/* Triangle styling based on A4 measurements */
.quote-triangle {
    position: absolute;
    bottom: -15.0mm;
    /* Position triangle directly below the box */
    left: 50%;
    transform: translateX(-50%);
    width: 15mm;
    height: 0;
    border-left: 19.3mm solid transparent;
    border-right: 19.3mm solid transparent;
    border-top: 15mm solid;
    /* Triangle height and color set dynamically */
}

.product-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 1px solid;
    /* Border color set dynamically */
}

.product-table th,
.product-table td {
    padding: 10px;
    text-align: center;
    border: 1px solid;
    /* Border color set dynamically */
}

.totals-table {
    position: absolute;
    top: 145mm;
    right: 5mm;
    width: 50%;
    border-collapse: collapse;
    border: 1px solid;
    /* Border color set dynamically */
}

.totals-table td {
    padding: 8px;
    font-weight: bold;
}

.contact-info div {
    flex: 1;
    display: flex;
    align-items: center;
}

.signature-section {
    margin-top: 40px;
}

.signature-line {
    width: 150mm;
    height: 1px;
    background-color: #000;
    margin: 10px auto;
}
</style>